import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Group, Skeleton, UnstyledButton } from "@mantine/core";

import ReleaseAssignmentMenu from "../../../releases/ReleaseAssignmentMenu";
import ReleaseChips from "../../../releases/ReleaseChips";
import { useReleases } from "../../../../hooks/useReleases";
import { useUserPermissions } from "../../../../hooks/useUserPermissions";
import { RELEASE_DELIVERY_ITEM_SUFFIX } from "../../../../constants/releases";
import { buildReleaseOptions } from "../../../../utils/releaseOptions";
import {
  deliveryOnlyReleaseIds,
  releaseIdsFromKey,
  releaseIdsKey,
  unionReleaseIds,
} from "../../../../utils/ideaReleases";
import { getNestedFieldValue } from "../../../ideaNiv/helpers/calculateImpactResults";

/**
 * Ideas-list cell for an idea's releases.
 *
 * Registered under the key `ReleaseCell` in `IdeasTable`'s `cellComponents`
 * map — the backend column registration names that exact `renderComponent`
 * string, and nothing type-checks across that seam.
 *
 * Same semantics as `IdeaReleaseField` on the detail page. The strip is the
 * union of the idea's own `releaseIds` and its linked delivery item's
 * (`productItem.releaseIds`, the registered accessor — a bare ObjectId array
 * that `ReleaseChips` resolves against `GET /releases`), the delivery item's
 * muted because two ideas can link to it. The picker writes only the idea's
 * own set, through `PUT /v1/ideas/:id/releases`, and needs no delivery link
 * to do it; `useIdeaReleases` patches the row in the list cache so the cell
 * re-seeds from it.
 */
export const ReleaseCell: React.FC<any> = ({ data, col }) => {
  const { releases, groups } = useReleases();
  const { canEditIdeasInline } = useUserPermissions();

  const options = useMemo(
    () => buildReleaseOptions(releases, groups),
    [releases, groups]
  );

  // Theme rows on the Bagel AI tab are aggregates, not ideas — the same
  // carve-out `StatusCell` makes.
  const isThemeRow = !!data?._isThemeRow;
  const ideaId = !isThemeRow && data?._id ? String(data._id) : undefined;

  const accessor = col?.accessor || "productItem.releaseIds";
  const linkedKey = releaseIdsKey(getNestedFieldValue(data, accessor));
  const linkedIds = useMemo(() => releaseIdsFromKey(linkedKey), [linkedKey]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const ownKey = releaseIdsKey(data?.releaseIds);
  useEffect(() => {
    setSelectedIds(releaseIdsFromKey(ownKey));
  }, [ownKey, data?._id]);

  const allReleaseIds = useMemo(
    () => unionReleaseIds(selectedIds, linkedIds),
    [selectedIds, linkedIds]
  );
  const deliveryOnlyIds = useMemo(
    () => deliveryOnlyReleaseIds(selectedIds, linkedIds),
    [selectedIds, linkedIds]
  );

  const columnWidth =
    typeof col?.width === "number" ? col.width : undefined;

  const chips = (
    <ReleaseChips
      releaseIds={allReleaseIds}
      options={options}
      columnWidth={columnWidth}
      dimmed={!ideaId}
      dimmedIds={deliveryOnlyIds}
      dimmedLabelSuffix={RELEASE_DELIVERY_ITEM_SUFFIX}
    />
  );

  return (
    <Box
      p={0}
      m={0}
      h="100%"
      w="100%"
      data-testid="release-cell"
      onMouseMove={(e) => e.stopPropagation()}
    >
      <ReleaseAssignmentMenu
        ideaId={ideaId}
        releaseIds={selectedIds}
        readOnly={!canEditIdeasInline}
        onSaved={setSelectedIds}
      >
        <UnstyledButton
          style={{
            width: columnWidth ? `${columnWidth}px` : "100%",
            maxWidth: columnWidth ? `${columnWidth}px` : "100%",
            textAlign: "left",
          }}
        >
          <Group
            noWrap
            spacing={6}
            sx={{
              "&:hover": { backgroundColor: "#F8F8FB" },
              cursor: ideaId && canEditIdeasInline ? "pointer" : "default",
              width: "100%",
              textAlign: "left",
              padding: "4px",
              borderRadius: "4px",
            }}
          >
            {chips}
          </Group>
        </UnstyledButton>
      </ReleaseAssignmentMenu>
    </Box>
  );
};

export const ReleaseCellSkeleton: React.FC<{ col: any }> = ({ col }) => {
  const columnWidth = col?.width;
  const chipWidths = columnWidth
    ? [Math.min(parseInt(columnWidth, 10) * 0.3, 64)]
    : [64];

  return (
    <Box sx={{ width: columnWidth || "100%", minWidth: col?.minWidth, maxWidth: col?.maxWidth }}>
      <Flex gap={4} wrap="wrap">
        {chipWidths.map((width, index) => (
          <Skeleton
            key={index}
            height={22}
            width={width}
            radius="xs"
            sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}
          />
        ))}
      </Flex>
    </Box>
  );
};

export default ReleaseCell;
